import { FormControl, FormGroup } from '@angular/forms';
import { Applicant } from '../interfaces/applicant';
import { Constructor, DatabaseRecord, HasActivityLogs } from '../mixins';
import { AgencyBlocAgent } from './agency-bloc-agent';
import { AgencyBlocEntity, GenericAgencyBlocEntity } from './agency-bloc-entities';
import { AgencyBlocGroup } from './agency-bloc-group';
import { AgencyBlocIndividual } from './agency-bloc-individual';
import { AgencyBlocPolicy } from './agency-bloc-policy';
import { FormSubmission } from './form-submission';
import { FormSubmissionType } from './form-submission-types';
import { Meeting } from './meeting';

export interface SearchCriteria {
  first_name?: string;
  last_name?: string;
  middle_name?: string;
}

export interface SearchResults {
  form: SearchResultsRecord;
  matches: AgencyBlocEntity[];
}

export interface FileObject {
  type?: string;
  file_name?: string;
  description?: string;
  created_at?: string;
  url?: {
    filename?: string;
    link?: string;
  };
}

/**
 * A record in the search results.
 */
export type SearchResultsRecordType = FormSubmission<FormSubmissionType> | Meeting | AgencyBlocEntity;

export class SearchResultsRecord extends HasActivityLogs(DatabaseRecord(HasEmailAttribute(HasFullNameAttribute()))) {
  /**
   * Additional information notes
   */
  public additionalInformation = '';

  /**
   * Is this record checked in the UI?
   */
  public checked = false;

  /**
   * Is this record from meeting
   */
  public meeting_id = 0;

  /**
   * saved pdf file
   */
  public file_name = '';

  /**
   * The agent that this is associated with
   */
  public agent?: AgencyBlocAgent;

  /**
   * Meeting Label
   */
  public Label?: string;

  /**
   * The user's partner during the form submission
   */
  public partner?: FormSubmission<FormSubmissionType>;

  /**
   * The user's applicants data during the form submission
   */
  public data?: {
    applicant: Applicant;
    partner: Applicant;
  };

  /**
   * The AgencyBlocGroup that this record is associated with
   */
  public group?: AgencyBlocGroup;

  /**
   * The AgencyBlocIndividual that this record is associated with
   */
  public individual?: AgencyBlocIndividual;

  /**
   * Is the data is secondary
   */
  public secondary_data = false;

  /**
   * Is the data is 3rd Party
   */
  public is_third_party = false;

  /**
   * Submitted forms files
   */
  public files: FileObject[] = [];

  /**
   * The unique AgencyBloc ID
   */
  public entityID?: number;

  /**
   * The type of entity this record represents. Can be 'Agent', 'Individual', 'Group', or 'Policy'.
   */
  public entityType?: string;

  /**
   * The form name
   */
  public form?: string;

  constructor(...args: any[]) {
    super(args);
    Object.assign(this, args[0]);
  }

  /**
   * Construct a new instance of the correct type
   * @param {SearchResultsRecord} record - The data to create the record from
   * @returns {typeof Meeting | typeof AgencyBlocAgent | typeof AgencyBlocIndividual | typeof AgencyBlocGroup | typeof AgencyBlocPolicy | typeof FormSubmission}
   */
  public static factory(record: SearchResultsRecordType) {
    // Check the record type using a property which is unique to the given record
    const x = { ...this };

    if (Object.prototype.hasOwnProperty.call(x, 'Label')) {
      return new Meeting(record as Meeting);
    } else if (Object.prototype.hasOwnProperty.call(record, '$entityType')) {
      record = record as unknown as AgencyBlocEntity;

      if (record.$entityType === 'Agent') {
        return new AgencyBlocAgent(record as AgencyBlocAgent);
      } else if (record.$entityType === 'Individual') {
        return new AgencyBlocIndividual(record as AgencyBlocIndividual);
      } else if (record.$entityType === 'Group') {
        return new AgencyBlocGroup(record as AgencyBlocGroup);
      } else if (record.$entityType === 'Policy') {
        return new AgencyBlocPolicy(record as AgencyBlocPolicy);
      }
    }

    // Assume the record is a FormSubmission
    return new FormSubmission(record as FormSubmission<FormSubmissionType>);
  }

  /**
   * Is this record of the given type?
   * @param {'Meeting' | 'FormSubmission' | 'AgencyBlocEntity'} type - The type to check
   *
   */
  public is(type: 'Meeting' | 'FormSubmission' | 'AgencyBlocEntity') {
    // Check the record type using a property which is unique to the given record
    const record = { ...this };

    if (type === 'Meeting' && record.hasOwnProperty('meeting_id') && record.meeting_id !== undefined && record.meeting_id !== null) {
      return true;
    } else if (type === 'AgencyBlocEntity' && record.hasOwnProperty('$entityType')) {
      return true;
    } else if (type === 'FormSubmission' && record.hasOwnProperty('form') && record.form !== undefined && record.form !== null) {
      return true;
    }

    return false;
  }

  /**
   * Extract all properties from this record which are relevant to a form submission
   * @returns {FormSubmission}
   */
  public formSubmission(): FormSubmission<FormSubmissionType> {
    return Object.assign(new FormSubmission(), this) as unknown as FormSubmission<FormSubmissionType>;
  }

  /**
   * Extract all properties from this record which are relevant to a meeting
   * @returns {Meeting}
   */
  public meeting(): Meeting {
    return Object.assign(new Meeting(), this) as Meeting;
  }

  /**
   * Extract all properties from this record which are relevant to an AgencyBloc entity
   * @returns {AgencyBlocEntity}
   */
  public agencyBlocEntity(): AgencyBlocEntity {
    return SearchResultsRecord.factory(Object.assign({}, this as unknown as AgencyBlocEntity)) as AgencyBlocEntity;
  }

  /**
   * Get the fields to compare when determining if two records are the same
   * @returns {string[]}
   */
  public get comparison_fields() {
    if (this.is('Meeting')) {
      if (this.entityType === 'Group') {
        return ['groupName', 'status', 'type', 'projectCode'];
      } else {
        return ['firstName', 'middleInitial', 'lastName', 'email', 'birthDate', 'maritalStatus', 'gender', 'phoneType', 'preferredPhone','leadDate', 'status', 'type', 'projectCode'];
      }
    } else if (this.is('AgencyBlocEntity')) return ['firstName', 'lastName', 'email', 'status', 'type', 'projectCode', 'leadDate'];
    else if (this.is('FormSubmission') && this.entityType === 'Group') return ['advisorGroupName', 'status', 'type', 'projectCode'];
    else if (this.is('FormSubmission') && this.entityType === 'Agent')
      return ['firstName', 'lastName', 'email', 'status', 'type', 'projectCode', 'thrivent_rfo', 'classification', 'title', 'leadDate'];
    // Assume the record is a FormSubmission
    return ['firstName', 'middleInitial', 'lastName', 'birthDate', 'gender', 'phoneType', 'preferredPhone', 'email','leadDate'];
  }

  /**
   * Create a new FormGroup for this record type
   */
  public createFormGroup(): FormGroup {
    if (this.is('Meeting')) {
      return new Meeting().createFormGroup(this.entityType);
    } else if (this.is('AgencyBlocEntity')) {
      const record = this as unknown as AgencyBlocEntity;
      if (record.$entityType === 'Agent') return new AgencyBlocAgent().createFormGroup();
      else if (record.$entityType === 'Individual') return new AgencyBlocIndividual().createFormGroup();
      else if (record.$entityType === 'Group') return new AgencyBlocGroup().createFormGroup();
      else if (record.$entityType === 'Policy') return new AgencyBlocPolicy().createFormGroup();
    } else if (this.is('FormSubmission') && this.entityType === 'Group') {
      return new AgencyBlocGroup().createFormGroup(this.entityType);
    } else if (this.is('FormSubmission') && this.entityType === 'Agent') {
      return new AgencyBlocAgent().createFormGroup('Agent');
    }

    // Assume the record is a FormSubmission
    return new FormSubmission().createFormGroup();
  }

  /**
   * Compares the current SearchResultsRecord instance with a given GenericAgencyBlocEntity record.
   * @param record - The GenericAgencyBlocEntity record to compare with.
   * @returns An object containing the comparison results for each field.
   */
  public compareTo(record: GenericAgencyBlocEntity): SearchResultsRecordComparesTo {
    let data: Partial<Applicant> | Meeting | undefined;
    if (this.is('FormSubmission')) {
      if(this.is_third_party){
        if (this.formSubmission().data) {
          data = this.formSubmission().data as Partial<Applicant>;
        }
      }else{
        data = this.secondary_data ? this.formSubmission().data?.partner : this.formSubmission().data?.applicant;
      }
    }

    if (this.is('Meeting')) {
      const meeting_data = JSON.parse(JSON.stringify(this));
      const birthDateKey = this.secondary_data ? 'Birthdate Client 2' : 'Birthdate Client 1';
      const emailKey = this.secondary_data ? 'Client Email 2' : 'Client Email';
      const maritalStatusKey = 'Partner Status';
      const phoneKey = this.secondary_data ? 'Client 2 Phone' : 'Client Phone';
      const phoneTypeKey = this.secondary_data ? 'Client 2 Phone Type' : 'Client Phone Type';
      const genderKey = this.secondary_data ? 'Gender Client 2' : 'Gender Client 1';

      data = {
        ...data,
        birthDate: meeting_data.data?.[birthDateKey] ?? '',
        email: meeting_data.data?.[emailKey] ?? '',
        maritalStatus: meeting_data.data?.[maritalStatusKey] ?? '',
        preferredPhone: meeting_data.data?.[phoneKey] ?? '',
        phoneType: meeting_data.data?.[phoneTypeKey] ?? '',
        gender: meeting_data.data?.[genderKey] ?? '',
      };
    } else if (this.data && this.is('FormSubmission') && this.entityType === 'Group') {
      data = {
        advisorGroupName: this.data.applicant.advisorGroupName,
      };
    }

    // Starting at -1 because the field_index is incremented before being used
    let field_index = -1;
    return this.comparison_fields.reduce((acc, key) => {
      if (data) {
        let compare_value = data[key as keyof typeof data]?.toString() || '';
        let with_value = record[key as keyof GenericAgencyBlocEntity]?.toString() || '';

        if (this.entityType === 'Group' && key === 'groupName') {
          compare_value = this.full_name;
        } else if (record.groupName && this.entityType === 'Group' && key === 'advisorGroupName') {
          with_value = record.groupName;
        }

        //Getting Data Preferred Phone No.
        if ((key === 'preferredPhone' || key === 'phoneType') && data['phoneType' as keyof typeof data]) {
          let phoneType = `${data['phoneType' as keyof typeof data]?.toString().toLowerCase()}Phone`;
          if (phoneType !== 'homePhone' && phoneType !== 'cellPhone') {
            phoneType = 'businessPhone';
          }
          if (key === 'preferredPhone') {
            with_value = record[phoneType as keyof GenericAgencyBlocEntity]?.toString() || '';
          }
          if (key === 'phoneType') {
            with_value = data['phoneType' as keyof typeof data]?.toString() ?? '';
          }
        }

        if(key==='leadDate'){
          compare_value = this['leadDtm' as keyof typeof this]?.toString() || '';
          with_value = record['leadDtm' as keyof GenericAgencyBlocEntity]?.toString() || '';
        }

        acc[key] = new FormGroup<SearchResultsRecordComparison>({
          [`useCompare${++field_index}`]: new FormControl<boolean>(true),
          compare: new FormControl<string | number | null | undefined>(compare_value),
          with: new FormControl<string | number | null | undefined>(with_value),
          key: new FormControl<string>(key),
        });
      }
      return acc;
    }, {} as SearchResultsRecordComparesTo);
  }
}

/**
 * If a record has an email, this mixin will add a getter and setter for it
 * @param Base
 */
export function HasEmailAttribute<T extends Constructor<NonNullable<unknown>>>(Base: T = class {} as any) {
  return class extends Base {
    protected _email = '';

    /**
     * Set the email of this record
     */
    public set email(value: string) {
      this._email = value;
    }

    /**
     * Get the email of this record
     */
    public get email(): string {
      return this._email;
    }
  };
}

/**
 * If a record has a full name, this mixin will add a getter and setter for it
 * @param Base
 */
export function HasFullNameAttribute<T extends Constructor<NonNullable<unknown>>>(Base: T = class {} as any) {
  return class extends Base {
    /**
     * Set the full name of this record
     */
    protected _full_name = '';

    /**
     * Set the full name of this record
     */
    public set full_name(value: string) {
      this._full_name = value;
    }

    /**
     * Get the full name of this record
     */
    public get full_name(): string {
      if (this._full_name.length) return this._full_name;

      const last = (this as any).lastName ?? '';
      const first = (this as any).firstName ?? '';
      return `${first} ${last}`.trim();
    }
  };
}

/**
 * Represents a comparison of search results records.
 */
interface SearchResultsRecordComparesTo {
  /**
   * The comparison results for each field.
   */
  [key: string]: FormGroup<SearchResultsRecordComparison>;
}

/**
 * Represents a comparison record in the search results.
 */
interface SearchResultsRecordComparison {
  /**
   * Indicates whether the origional field should be saved or the compared field.
   */
  [key: string]: FormControl<boolean | string | number | null | undefined>;

  /**
   * The value of the field in the origional record.
   */
  compare: FormControl<string | number | null | undefined>;

  /**
   * The value of the field in the compared record.
   */
  with: FormControl<string | number | null | undefined>;

  /**
   * The key of the field.
   */
  key: FormControl<string | null>;
}
